/**
 * ComparisonGrid — Side-by-side comparison of 2-3 items
 *
 * Grid layout with items as columns, attributes as rows.
 * Highlights best values and shows "winner" indicators.
 */
import React from 'react';
import type { ComparisonGridData } from '../types';

interface ComparisonGridProps {
  data: ComparisonGridData;
}

/**
 * Determine the "best" value index for a given attribute row.
 * Returns the index of the winning item, or -1 if no clear winner.
 */
function findBestIndex(
  values: (string | number | boolean)[],
  higherIsBetter?: boolean
): number {
  if (values.length === 0) return -1;

  // Boolean: true wins
  if (typeof values[0] === 'boolean') {
    const trueCount = values.filter((v) => v === true).length;
    if (trueCount === values.length || trueCount === 0) return -1;
    return values.indexOf(true);
  }

  // Number: compare by higherIsBetter (default true)
  if (typeof values[0] === 'number') {
    const nums = values as number[];
    const allSame = nums.every((n) => n === nums[0]);
    if (allSame) return -1;
    const better = higherIsBetter !== false;
    const target = better ? Math.max(...nums) : Math.min(...nums);
    return nums.indexOf(target);
  }

  // Strings: no automatic winner
  return -1;
}

function formatValue(value: string | number | boolean, unit?: string): string {
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number') {
    const formatted = Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return unit ? `${formatted} ${unit}` : formatted;
  }
  return unit ? `${value} ${unit}` : String(value);
}

export const ComparisonGrid: React.FC<ComparisonGridProps> = ({ data }) => {
  const { items, highlightBest = true, attributes } = data;

  if (!items || items.length === 0) return null;

  // Derive attribute keys from explicit list or first item
  const attrList: { key: string; label: string; unit?: string; higherIsBetter?: boolean }[] =
    attributes && attributes.length > 0
      ? attributes
      : Object.keys(items[0].attributes).map((key) => ({
          key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        }));

  return (
    <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/20 shadow-[0_0_20px_rgba(34,211,238,0.08)] overflow-hidden">
      {/* Scrollable wrapper for mobile */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          {/* Header: Item names */}
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-black/80 backdrop-blur-sm px-4 py-3 text-left text-[10px] uppercase tracking-wider text-cyan-400/50 font-bold border-b border-cyan-400/10 min-w-[120px]">
                Attribute
              </th>
              {items.map((item) => (
                <th
                  key={item.id}
                  className="px-4 py-3 text-center border-b border-cyan-400/10 min-w-[140px]"
                >
                  <div className="flex flex-col items-center gap-2">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-10 h-10 rounded-lg object-cover border border-cyan-400/20"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <span className="text-sm font-bold text-cyan-300">
                      {item.name}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body: Attribute rows */}
          <tbody>
            {attrList.map((attr, rowIdx) => {
              const values = items.map(
                (item) => item.attributes[attr.key] ?? '—'
              );
              const actualBestIdx = highlightBest
                ? findBestIndex(values as (string | number | boolean)[], attr.higherIsBetter)
                : -1;

              return (
                <tr
                  key={attr.key}
                  className={`transition-colors duration-150 ${
                    rowIdx % 2 === 1 ? 'bg-white/[0.02]' : ''
                  } hover:bg-cyan-400/[0.04]`}
                >
                  {/* Sticky attribute label */}
                  <td className="sticky left-0 z-10 bg-black/80 backdrop-blur-sm px-4 py-3 text-xs font-medium text-cyan-400/60 border-b border-cyan-400/5 min-w-[120px]">
                    {attr.label}
                  </td>

                  {/* Values */}
                  {values.map((value, colIdx) => {
                    const isWinner = actualBestIdx === colIdx && actualBestIdx !== -1;
                    const isBoolTrue = typeof value === 'boolean' && value;
                    const isBoolFalse = typeof value === 'boolean' && !value;

                    return (
                      <td
                        key={`${attr.key}-${colIdx}`}
                        className={`px-4 py-3 text-center text-sm border-b border-cyan-400/5 transition-all duration-200 ${
                          isWinner
                            ? 'bg-cyan-400/[0.08] text-cyan-300 font-semibold'
                            : isBoolTrue
                              ? 'text-emerald-400'
                              : isBoolFalse
                                ? 'text-red-400/60'
                                : 'text-cyan-300/70'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          {isWinner && (
                            <span className="text-cyan-400 text-[10px]">★</span>
                          )}
                          <span>
                            {value === '—'
                              ? '—'
                              : formatValue(
                                  value as string | number | boolean,
                                  attr.unit
                                )}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonGrid;
